import classNames from "classnames";
import {ForwardedRef, forwardRef} from "react";
import {components, MultiValueGenericProps, OptionProps, SelectInstance, SingleValueProps} from "react-select";

import {Select} from "@/components/select/Select.tsx";
import {SelectProps} from "@/components/select/Select.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {MonitoringGroup, MonitoringGroupLevel} from "@/lib/types/monitoring.ts";

const LocationOption =
  (limitMonitoringGroupLevel?: MonitoringGroupLevel) =>
  ({data, ...props}: OptionProps<MonitoringGroup>) => {
    const flag = data.country.toLowerCase();
    const aboveTier = limitMonitoringGroupLevel && data.level > limitMonitoringGroupLevel;
    return (
      <components.Option {...props} data={data}>
        <Tooltip
          tooltipContent={
            aboveTier
              ? "This Probe Server Location is not available on your current plan. Upgrade to add this probe server monitor."
              : undefined
          }
        >
          <span className={classNames("d-flex", {"pulse-color-gray-500": aboveTier})}>
            <span className="me-auto">
              {flag && <span className={`me-2 fi fi-${flag}`} />}
              {data.location}
            </span>
            <span>{data.code}</span>
          </span>
        </Tooltip>
      </components.Option>
    );
  };

const SingleValueComponent = ({children, data, ...props}: SingleValueProps<MonitoringGroup>) => {
  const flag = data.country.toLowerCase();
  return (
    <components.SingleValue {...props} data={data}>
      {flag && <span className={`me-2 fi fi-${flag}`} />}
      {children}
    </components.SingleValue>
  );
};

const MultiValueLabel = ({children, data, ...props}: MultiValueGenericProps<MonitoringGroup>) => {
  const flag = data.country.toLowerCase();
  return (
    <components.MultiValueLabel {...props} data={data}>
      <span className="cursor-text">
        {flag && <span className={`me-2 fi fi-${flag}`} />}
        {children}
      </span>
    </components.MultiValueLabel>
  );
};

export const LocationSelect = forwardRef(
  (
    {
      limitMonitoringGroupLevel,
      ...props
    }: SelectProps<MonitoringGroup, boolean> & {limitMonitoringGroupLevel?: MonitoringGroupLevel},
    ref: ForwardedRef<SelectInstance<MonitoringGroup, boolean>>
  ) => {
    return (
      <Select<MonitoringGroup, boolean>
        components={{
          Option: LocationOption(limitMonitoringGroupLevel),
          SingleValue: SingleValueComponent,
          MultiValueLabel: MultiValueLabel,
        }}
        getOptionLabel={(mg) => mg.location}
        getOptionValue={(mg) => mg.id.toString()}
        {...props}
        ref={ref}
      />
    );
  }
);
